import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardList,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  ChevronDown,
  X,
  Phone,
  User,
  Calendar,
  Hash,
  AlertCircle,
  MessageSquare,
  Lock,
  Send,
  RefreshCw,
  Trash2,
} from "lucide-react";
import authAxios from "../api/authAxios";
import toast from "react-hot-toast";

const STATUS_COLORS = {
  "Pending Approval": {
    bg: "bg-amber-50 dark:bg-amber-500/10",
    text: "text-amber-700 dark:text-amber-400",
    border: "border-amber-200 dark:border-amber-500/30",
    dot: "bg-amber-500",
    icon: Clock,
  },
  Approved: {
    bg: "bg-emerald-50 dark:bg-emerald-500/10",
    text: "text-emerald-700 dark:text-emerald-400",
    border: "border-emerald-200 dark:border-emerald-500/30",
    dot: "bg-emerald-500",
    icon: CheckCircle,
  },
  Rejected: {
    bg: "bg-red-50 dark:bg-red-500/10",
    text: "text-red-700 dark:text-red-400",
    border: "border-red-200 dark:border-red-500/30",
    dot: "bg-red-500",
    icon: XCircle,
  },
};

function StatusBadge({ status }) {
  const config = STATUS_COLORS[status] || STATUS_COLORS["Pending Approval"];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${config.bg} ${config.text} ${config.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {status}
    </span>
  );
}

function formatDateTime(isoString) {
  if (!isoString) return "—";
  const d = new Date(isoString);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDate(isoString) {
  if (!isoString) return "—";
  const d = new Date(isoString);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatTime(isoString) {
  if (!isoString) return "—";
  const d = new Date(isoString);
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function DetailField({ icon: Icon, label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide mb-1 flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </p>
      <p className="text-sm font-semibold text-gray-900 dark:text-white">{value || "—"}</p>
    </div>
  );
}

function ViewModal({ request, onClose, onApprove, onReject, onWhatsApp }) {
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [password, setPassword] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentReq, setCurrentReq] = useState(request);

  const handleApprove = async () => {
    if (!password) { toast.error("Please set a login password."); return; }
    setLoading(true);
    try {
      await authAxios.post(`/api/auth/account-requests/${currentReq.id}/approve/`, { password });
      toast.success(`${currentReq.request_id} approved successfully!`);
      const updated = {
        ...currentReq,
        status: "Approved",
        approved_at: new Date().toISOString(),
        approved_by: "Admin",
        login_password: password,
      };
      setCurrentReq(updated);
      onApprove(currentReq.id, password);
      setShowApprove(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve.");
    } finally { setLoading(false); }
  };

  const handleReject = async () => {
    setLoading(true);
    try {
      await authAxios.post(`/api/auth/account-requests/${currentReq.id}/reject/`, { reason: rejectionReason });
      toast.success(`${currentReq.request_id} rejected.`);
      const updated = {
        ...currentReq,
        status: "Rejected",
        rejected_at: new Date().toISOString(),
        rejected_by: "Admin",
        rejection_reason: rejectionReason,
      };
      setCurrentReq(updated);
      onReject(currentReq.id);
      setShowReject(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto"
      >
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-5 flex items-center justify-between sticky top-0 z-10">
          <div>
            <p className="text-green-100 text-xs font-semibold uppercase tracking-widest">Account Registration Details</p>
            <h2 className="text-white text-xl font-bold mt-1">{currentReq.request_id}</h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-2 rounded-xl hover:bg-white/10 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <DetailField icon={Hash} label="Request ID" value={currentReq.request_id} />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide mb-1">Status</p>
              <StatusBadge status={currentReq.status} />
            </div>
            <DetailField icon={User} label="Full Name" value={currentReq.full_name} />
            <DetailField icon={Phone} label="Mobile Number" value={currentReq.mobile_number} />
            <DetailField icon={Calendar} label="Registration Date" value={formatDate(currentReq.created_at)} />
            <DetailField icon={Clock} label="Registration Time" value={formatTime(currentReq.created_at)} />
          </div>

          {currentReq.status === "Approved" && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl p-4 border border-emerald-200 dark:border-emerald-500/30 space-y-3">
              <p className="text-xs font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide">Approval Info</p>
              <div className="space-y-1">
                <p className="text-sm text-gray-700 dark:text-gray-300">Approved by: <span className="font-semibold">{currentReq.approved_by || "Admin"}</span></p>
                <p className="text-sm text-gray-700 dark:text-gray-300">Approved at: <span className="font-semibold">{formatDateTime(currentReq.approved_at)}</span></p>
                {currentReq.login_password && (
                  <p className="text-sm text-gray-700 dark:text-gray-300">Temporary Password: <span className="font-mono font-bold text-emerald-700 dark:text-emerald-300">{currentReq.login_password}</span></p>
                )}
              </div>
              <button
                type="button"
                onClick={() => onWhatsApp && onWhatsApp(currentReq, currentReq.login_password)}
                className="w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors shadow-sm"
              >
                <Send className="w-4 h-4" /> Share Credentials via WhatsApp
              </button>
            </div>
          )}

          {currentReq.status === "Rejected" && (
            <div className="bg-red-50 dark:bg-red-900/20 rounded-2xl p-4 border border-red-200 dark:border-red-500/30 space-y-1">
              <p className="text-xs font-bold text-red-700 dark:text-red-400 uppercase tracking-wide mb-2">Rejection Info</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">Rejected by: <span className="font-semibold">{currentReq.rejected_by || "Admin"}</span></p>
              <p className="text-sm text-gray-700 dark:text-gray-300">Rejected at: <span className="font-semibold">{formatDateTime(currentReq.rejected_at)}</span></p>
              {currentReq.rejection_reason && (
                <p className="text-sm text-gray-700 dark:text-gray-300">Reason: <span className="font-semibold">{currentReq.rejection_reason}</span></p>
              )}
            </div>
          )}

          {request.status === "Pending Approval" && (
            <div className="border-t border-gray-100 dark:border-gray-800 pt-4 space-y-3">
              {/* Approve */}
              <button
                onClick={() => { setShowApprove(!showApprove); setShowReject(false); }}
                className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 text-emerald-700 dark:text-emerald-400 font-semibold text-sm transition-colors border border-emerald-200 dark:border-emerald-500/30"
              >
                <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Approve Request</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showApprove ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {showApprove && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                    <div className="bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl p-4 space-y-3 border border-emerald-200 dark:border-emerald-500/20">
                      <label className="block">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide flex items-center gap-1 mb-1.5">
                          <Lock className="w-3 h-3" /> Login Password (required)
                        </span>
                        <input
                          type="text"
                          placeholder="Set a login password for this user"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full px-4 py-2.5 rounded-xl border border-emerald-200 dark:border-emerald-500/30 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </label>
                      <button
                        onClick={handleApprove}
                        disabled={loading || !password}
                        className="w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors"
                      >
                        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                        Approve & Set Password
                      </button>
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1">
                        <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        After approval, share credentials via WhatsApp.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Reject */}
              <button
                onClick={() => { setShowReject(!showReject); setShowApprove(false); }}
                className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-red-50 dark:bg-red-900/20 hover:bg-red-100 text-red-700 dark:text-red-400 font-semibold text-sm transition-colors border border-red-200 dark:border-red-500/30"
              >
                <span className="flex items-center gap-2"><XCircle className="w-4 h-4" /> Reject Request</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showReject ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {showReject && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                    <div className="bg-red-50 dark:bg-red-900/10 rounded-2xl p-4 space-y-3 border border-red-200 dark:border-red-500/20">
                      <label className="block">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide flex items-center gap-1 mb-1.5">
                          <MessageSquare className="w-3 h-3" /> Rejection Reason (optional)
                        </span>
                        <textarea
                          placeholder="Enter reason for rejection..."
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          rows={3}
                          className="w-full px-4 py-2.5 rounded-xl border border-red-200 dark:border-red-500/30 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                        />
                      </label>
                      <button
                        onClick={handleReject}
                        disabled={loading}
                        className="w-full py-2.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors"
                      >
                        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                        Confirm Rejection
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default function AccountRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const res = await authAxios.get(`/api/auth/account-requests/?${params.toString()}`);
      setRequests(res.data.results || []);
      setCounts(res.data.counts || { pending: 0, approved: 0, rejected: 0, total: 0 });
    } catch (err) {
      toast.error("Failed to load account requests.");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, dateFrom, dateTo]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleApprove = (id, password) => {
    setRequests((prev) => prev.map((r) => r.id === id ? { ...r, status: "Approved", login_password: password } : r));
    setCounts((prev) => ({ ...prev, pending: Math.max(0, prev.pending - 1), approved: prev.approved + 1 }));
  };

  const handleReject = (id) => {
    setRequests((prev) => prev.map((r) => r.id === id ? { ...r, status: "Rejected" } : r));
    setCounts((prev) => ({ ...prev, pending: Math.max(0, prev.pending - 1), rejected: prev.rejected + 1 }));
  };

  const confirmDelete = async () => {
    if (!requestToDelete) return;
    setIsDeleting(true);
    try {
      await authAxios.delete(`/api/auth/account-requests/${requestToDelete.id}/delete/`);
      setRequests((prev) => prev.filter((r) => r.id !== requestToDelete.id));
      setCounts((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      toast.success("Request deleted successfully.");
      setRequestToDelete(null);
    } catch (err) {
      toast.error("Failed to delete request.");
    } finally {
      setIsDeleting(false);
    }
  };

  const openWhatsApp = (req, pwd) => {
    const passwordText = pwd || req.login_password || "";
    const message = encodeURIComponent(
      `Hello ${req.full_name},\nYour Gio Billing account has been approved.\nMobile: ${req.mobile_number}\nPassword: ${passwordText}\nYou can now log in to the app.`
    );
    window.open(`https://wa.me/91${req.mobile_number}?text=${message}`, "_blank");
  };

  const STATS = [
    { label: "Total Requests", value: counts.total, color: "blue", icon: ClipboardList },
    { label: "Pending Approval", value: counts.pending, color: "amber", icon: Clock },
    { label: "Approved", value: counts.approved, color: "emerald", icon: CheckCircle },
    { label: "Rejected", value: counts.rejected, color: "red", icon: XCircle },
  ];

  const COLOR_MAP = {
    blue: "from-blue-500/10 to-blue-600/10 border-blue-200 dark:border-blue-500/30 text-blue-600 dark:text-blue-400",
    amber: "from-amber-500/10 to-amber-600/10 border-amber-200 dark:border-amber-500/30 text-amber-600 dark:text-amber-400",
    emerald: "from-emerald-500/10 to-emerald-600/10 border-emerald-200 dark:border-emerald-500/30 text-emerald-600 dark:text-emerald-400",
    red: "from-red-500/10 to-red-600/10 border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400",
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
          <ClipboardList className="w-5 h-5 text-green-600 dark:text-green-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Account Requests</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage account registration requests submitted from the Gio Billing mobile app
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {STATS.map((stat) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              whileHover={{ y: -2 }}
              className={`bg-gradient-to-br ${COLOR_MAP[stat.color]} bg-white dark:bg-gray-800/50 rounded-2xl p-4 border`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 opacity-70" />
                <p className="text-xs font-semibold opacity-70">{stat.label}</p>
              </div>
              <p className="text-3xl font-black">{loading ? "..." : stat.value}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 mb-4 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              id="account-req-search"
              type="text"
              placeholder="Search by name, mobile, or request ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              id="account-req-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-9 pr-8 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 appearance-none cursor-pointer"
            >
              <option value="">All Statuses</option>
              <option value="Pending Approval">Pending Approval</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              title="From date"
              className="pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              title="To date"
              className="pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          {(search || statusFilter || dateFrom || dateTo) && (
            <button
              onClick={() => { setSearch(""); setStatusFilter(""); setDateFrom(""); setDateTo(""); }}
              className="flex items-center gap-1 px-3 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <X className="w-4 h-4" /> Clear
            </button>
          )}
          <button
            onClick={fetchRequests}
            className="flex items-center gap-1 px-3 py-2.5 rounded-xl bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-green-500 animate-spin mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Loading account requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
              <ClipboardList className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 font-semibold">No account requests found</p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
              New requests from the Gio Billing mobile app will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
                  {["Request ID", "Full Name", "Mobile Number", "Registration Date & Time", "Status", "Action"].map((h) => (
                    <th key={h} className="text-left px-3 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {requests.map((req, i) => (
                    <motion.tr
                      key={req.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="px-3 py-3">
                        <span className="font-mono font-bold text-green-700 dark:text-green-400 text-xs bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-md">
                          {req.request_id}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {req.full_name?.[0]?.toUpperCase() || "?"}
                          </div>
                          <span className="font-semibold text-gray-900 dark:text-white text-sm whitespace-nowrap">{req.full_name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-xs text-gray-700 dark:text-gray-300 font-medium font-mono">{req.mobile_number}</span>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{formatDateTime(req.created_at)}</span>
                      </td>
                      <td className="px-3 py-3">
                        <StatusBadge status={req.status} />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            id={`view-req-${req.id}`}
                            onClick={() => setSelectedRequest(req)}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-xs font-semibold hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors border border-blue-200 dark:border-blue-500/30 whitespace-nowrap"
                          >
                            <Eye className="w-3 h-3" /> View
                          </button>
                          {req.status === "Approved" && req.login_password && (
                            <button
                              id={`whatsapp-req-${req.id}`}
                              onClick={() => openWhatsApp(req)}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs font-semibold hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors border border-green-200 dark:border-green-500/30 whitespace-nowrap"
                            >
                              <Send className="w-3 h-3" /> WhatsApp
                            </button>
                          )}
                          <button
                            id={`delete-req-${req.id}`}
                            onClick={() => setRequestToDelete(req)}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs font-semibold hover:bg-red-600 hover:text-white transition-colors border border-red-200 dark:border-red-500/30 whitespace-nowrap"
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View / Details Modal */}
      <AnimatePresence>
        {selectedRequest && (
          <ViewModal
            request={selectedRequest}
            onClose={() => { setSelectedRequest(null); fetchRequests(); }}
            onApprove={handleApprove}
            onReject={handleReject}
            onWhatsApp={openWhatsApp}
          />
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal Box */}
      <AnimatePresence>
        {requestToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden p-6 border border-gray-100 dark:border-gray-700"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-2xl bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mb-4">
                  <Trash2 className="w-7 h-7" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                  Delete Request?
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Are you sure you want to permanently delete{" "}
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {requestToDelete.full_name} ({requestToDelete.request_id})
                  </span>
                  ? This action cannot be undone.
                </p>
                <div className="flex items-center gap-3 w-full">
                  <button
                    type="button"
                    onClick={() => setRequestToDelete(null)}
                    disabled={isDeleting}
                    className="flex-1 py-2.5 px-4 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmDelete}
                    disabled={isDeleting}
                    className="flex-1 py-2.5 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors shadow-lg shadow-red-600/30 flex items-center justify-center gap-2"
                  >
                    {isDeleting ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" /> Deleting...
                      </>
                    ) : (
                      "Delete"
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
